import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Input, Button, Table, Alert, StatCard, PageHeader, Tag, Tooltip, message } from '../../ui';
import {
  Sparkles, Database, Table2, Columns3, ShieldCheck, Copy, Check, TriangleAlert,
  PlugZap, CornerDownLeft, Lightbulb, ChevronRight,
} from 'lucide-react';
import { request } from '../../api/client';
import { inr2, fmtQty } from '../../lib/format';

/**
 * Questions that ARE the documentation. A blank box with a "ask me anything" promise is the hardest
 * thing to start from — these say, by example, the shape of question this can answer and the kind of
 * data it reaches, and one click runs it.
 */
const EXAMPLES = [
  'Top 10 retailers by total order value this year',
  'Monthly order value for the last 6 months',
  'Top 10 products by quantity sold last quarter',
  'How many orders per territory this month?',
  'Which retailers have not ordered in the last 60 days?',
  'Open complaints by category',
  'Stock on hand per warehouse for each product',
  'Visits per sales person last month',
];

/** `total_order_value` → "Total order value". Column keys arrive as raw SQL aliases. */
const humanColumn = (k: string) =>
  String(k).replace(/[_.]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^./, (c) => c.toUpperCase());

/** A column is money when its NAME says so — the only signal a free-form query gives us. */
const MONEY_KEY = /(amount|amt|value|total|revenue|sales|price|paid|due|outstanding|balance|grand)/i;
const QTY_KEY = /(qty|quantity|count|orders|units|visits|days|num|no_of)/i;

/** Render one cell: money and quantities get number treatment, everything else stays as it is. */
function cellFor(key: string, v: any): ReactNode {
  if (v == null || v === '') return <span className="text-subtle">—</span>;
  const n = Number(v);
  if (typeof v !== 'boolean' && v !== '' && Number.isFinite(n) && /^-?\d*\.?\d+$/.test(String(v).trim())) {
    if (MONEY_KEY.test(key) && !QTY_KEY.test(key)) return <span className="tabular-nums">{inr2(n)}</span>;
    return <span className="tabular-nums">{fmtQty(n)}</span>;
  }
  // A DATETIME comes back as an ISO string; show the date part, which is what a report wants.
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return <span className="tabular-nums">{s.slice(0, 10)}</span>;
  return s;
}

/**
 * The engineering half of a message, folded away.
 *
 * "Ollama error: connect ETIMEDOUT 160.30.126.34:11434" tells the person who can fix it exactly what
 * broke, and tells everyone else that the software is broken. The plain sentence goes above; this
 * keeps the detail one click away instead of deleting it.
 */
function TechDetail({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1.5">
      <button
        type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="inline-flex items-center gap-1 type-caption text-subtle underline-offset-2 hover:underline"
      >
        <ChevronRight size={12} className={open ? 'rotate-90 transition-transform' : 'transition-transform'} />
        Technical detail
      </button>
      {open && (
        <div className="mt-1 rounded-control border border-line bg-surface-2 px-2.5 py-1.5 type-caption text-muted">
          {children}
        </div>
      )}
    </div>
  );
}

/** Copy-to-clipboard that says it worked, for the generated SQL. */
function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      size="sm" variant="ghost" icon={done ? <Check size={14} /> : <Copy size={14} />}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true); setTimeout(() => setDone(false), 1600);
        } catch { message.error('Could not copy'); }
      }}
    >
      {done ? 'Copied' : 'Copy SQL'}
    </Button>
  );
}

/**
 * ASK THE DATA — a question in English becomes a read-only lookup over a fixed set of tables.
 *
 * WHO IS READING THIS. A sales manager, not an engineer. So the page speaks their language first:
 * what it can be asked about, how big the answer is, and what to do when it fails. The generated SQL
 * and the raw error text still exist — a number produced by a language model needs provenance — but
 * they live behind "show how this was worked out" and "technical detail" rather than greeting
 * everyone with a wall of code and a socket timeout.
 */
export function NlAnalyticsPage() {
  const [q, setQ] = useState('Top 10 retailers by total order value this year');
  const [asked, setAsked] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [showSql, setShowSql] = useState(false);

  const { data: caps } = useQuery({
    queryKey: ['nl-capabilities'],
    queryFn: () => request<any>({ url: '/ai/analytics/nl/capabilities' }),
  });

  const ask = async (question: string) => {
    const text = question.trim();
    if (!text) return;
    setLoading(true); setAsked(text); setResult(null); setShowSql(false);
    try {
      setResult(await request<any>({ url: '/ai/analytics/nl', method: 'POST', data: { question: text } }));
    } catch (e: any) {
      setResult({ error: e.response?.data?.error?.message ?? 'The question could not be answered.' });
    } finally {
      setLoading(false);
    }
  };

  const rows: any[] = result?.rows ?? [];
  const keys = useMemo(() => (rows[0] ? Object.keys(rows[0]) : []), [rows]);
  const columns = useMemo(
    () => keys.map((k) => ({
      title: humanColumn(k),
      dataIndex: k,
      align: (MONEY_KEY.test(k) || QTY_KEY.test(k) ? 'right' : 'left') as 'right' | 'left',
      render: (v: any) => cellFor(k, v),
    })),
    [keys],
  );
  // The executor wraps every query in `LIMIT 200`; landing exactly on it means rows were cut off.
  const capped = rows.length >= (caps?.rowLimit ?? 200);

  return (
    <div>
      <PageHeader
        icon={<Sparkles />}
        title="Ask the Data"
        subtitle="Ask a question in plain English and get the rows back. Nothing here can change your data — it only ever reads."
      />

      {/* The ask box. Enter submits, because this is a question box, not a form. */}
      <Card className="mb-4">
        <label className="mb-1.5 block type-label-strong text-content" htmlFor="nl-question">Your question</label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id="nl-question"
            className="min-w-[260px] flex-1"
            size="large"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') ask(q); }}
            placeholder="e.g. Top 10 SKUs in Maharashtra last quarter"
          />
          <Button variant="primary" size="large" loading={loading} icon={<CornerDownLeft size={16} />} onClick={() => ask(q)}>
            Ask
          </Button>
        </div>

        <div className="mt-3">
          <div className="mb-1.5 flex items-center gap-1.5 type-overline text-subtle">
            <Lightbulb size={12} /> Try one of these
          </div>
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLES.map((ex) => (
              <button
                key={ex} type="button" disabled={loading}
                onClick={() => { setQ(ex); ask(ex); }}
                className="rounded-pill border border-line bg-surface-2 px-2.5 py-1 type-label text-muted transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 disabled:opacity-50 dark:hover:bg-brand-900/25 dark:hover:text-brand-300"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>

        {/* The honest boundary of the feature, straight from the server's own whitelist. */}
        {caps?.tables?.length && (
          <div className="mt-3 border-t border-line pt-3">
            <div className="mb-1.5 flex flex-wrap items-center gap-2 type-overline text-subtle">
              <span className="inline-flex items-center gap-1.5"><Database size={12} /> It can read these {caps.tables.length} tables</span>
              <Tooltip label="Only SELECT statements are allowed, and only against these tables. Anything else is rejected before it runs.">
                <span className="inline-flex items-center gap-1 rounded-pill bg-brand-500/10 px-2 py-0.5 type-label text-brand-700 dark:text-brand-300">
                  <ShieldCheck size={11} /> read-only
                </span>
              </Tooltip>
            </div>
            <div className="flex flex-wrap gap-1">
              {caps.tables.map((t: string) => (
                <span key={t} className="rounded-control bg-surface-3 px-1.5 py-0.5 type-caption tabular-nums text-muted">{t}</span>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Plain language first. The person reading this wants to know whether they can work, not which
          environment variable is unset — that detail is one click away for whoever can act on it. */}
      {caps && !caps.configured && (
        <Alert
          className="mb-4" type="warning" icon={<PlugZap size={16} />}
          title="The assistant isn’t switched on yet"
          description={
            <>
              <div>Questions can’t be answered until it is set up. Nothing is wrong with your data.</div>
              <TechDetail>No language model is configured on the API (OLLAMA_URL / OLLAMA_MODEL).</TechDetail>
            </>
          }
        />
      )}

      {loading && (
        <Card>
          <div className="py-8 text-center type-body text-muted">
            Translating “{asked}” into SQL…
          </div>
        </Card>
      )}

      {!loading && result?.error && (() => {
        // Three things can go wrong, and each needs a different sentence from the reader's side:
        // the assistant is not answering, the question could not be turned into a lookup, or the
        // lookup itself failed. The raw message is engineering detail — kept, but folded away.
        const raw = String(result.error);
        const unreachable = /ollama|ETIMEDOUT|ECONNREFUSED|ENOTFOUND|not configured/i.test(raw);
        const rejected = /unsafe|rejected/i.test(raw);
        return (
          <Alert
            className="mb-4" type={unreachable ? 'warning' : 'error'} icon={<TriangleAlert size={16} />}
            title={
              unreachable ? 'The assistant isn’t responding right now'
                : rejected ? 'That question can’t be answered safely'
                  : 'That question couldn’t be answered'
            }
            description={
              <>
                <div>
                  {unreachable
                    ? 'Your data is fine — the assistant that turns questions into lookups can’t be reached. Try again in a moment.'
                    : rejected
                      ? 'It would have needed data this page isn’t allowed to read. Try asking about one of the areas listed above.'
                      : 'Try rewording it, or start from one of the examples above — shorter, more specific questions work best.'}
                </div>
                <TechDetail>{raw}</TechDetail>
              </>
            }
          />
        );
      })()}

      {!loading && result && !result.error && (
        <>
          {/* What came back, in the reader's terms. "Tables read" and "query type" were engineering
              trivia dressed as KPIs — the person asking wants to know how much of an answer this is
              and where it came from, not which statement verb ran. */}
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3">
            <StatCard
              tone="brand" icon={<Table2 size={18} />} label="Rows in the answer"
              value={rows.length}
              hint={capped ? `cut off at ${caps?.rowLimit ?? 200} — ask for a narrower slice` : 'the complete answer'}
            />
            <StatCard
              tone="sky" icon={<Columns3 size={18} />} label="Columns"
              value={keys.length}
              hint={keys.length ? keys.slice(0, 3).map(humanColumn).join(' · ') + (keys.length > 3 ? ' …' : '') : 'no columns'}
            />
            <StatCard
              tone="neutral" icon={<Database size={18} />} label="Looked at"
              value={tablesIn(result.sql).length || '—'}
              hint={tablesIn(result.sql).map(humanColumn).join(', ').toLowerCase() || 'nothing identified'}
            />
          </div>

          {/* The model's own sentence about what it did — the closest thing to an answer in words. */}
          {result.explain && (
            <Alert className="mb-4" type="info" icon={<Sparkles size={16} />} title={result.explain} />
          )}

          <Card title={`Result${asked ? ` · ${asked}` : ''}`}>
            {capped && (
              <Alert
                className="mb-3" type="warning"
                title={`Showing the first ${caps?.rowLimit ?? 200} rows`}
                description="Every query runs under a row cap. Narrow the question — add a period, a territory or a top-N — to see a complete answer."
              />
            )}
            <Table
              rowKey={(_: any, i?: number) => String(i)}
              dataSource={rows}
              columns={columns as any}
              size="small"
              pagination={rows.length > 25 ? { pageSize: 25 } : false}
              locale={{ emptyText: 'Nothing matched that question' }}
            />

            {/* The lookup itself, folded away. It is the only way to check what was actually counted —
                so it stays reachable — but a page of SQL is not what a sales manager came here to read,
                and it used to open as a card of its own above the answer. */}
            {result.sql && (
              <div className="mt-3 border-t border-line pt-3">
                <button
                  type="button" onClick={() => setShowSql((v) => !v)} aria-expanded={showSql}
                  className="inline-flex items-center gap-1.5 type-caption text-muted transition-colors hover:text-brand-700 dark:hover:text-brand-300"
                >
                  <ChevronRight size={13} className={showSql ? 'rotate-90 transition-transform' : 'transition-transform'} />
                  {showSql ? 'Hide how this was worked out' : 'Show how this was worked out'}
                </button>
                {showSql && (
                  <div className="mt-2">
                    <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                      <span className="type-caption text-subtle">The exact read-only lookup that produced these rows.</span>
                      <CopyButton text={result.sql} />
                    </div>
                    <pre className="overflow-x-auto rounded-control border border-line bg-surface-2 p-3 type-caption tabular-nums text-content">
                      {result.sql}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </Card>
        </>
      )}

      {!loading && !result && (
        <Card>
          <div className="py-10 text-center">
            <span className="mx-auto mb-3 grid size-12 place-items-center rounded-pill bg-brand-500/10 text-brand-700 dark:text-brand-300">
              <Sparkles size={22} />
            </span>
            <div className="type-section-title">Ask your first question</div>
            <p className="mx-auto mt-1 max-w-[520px] type-body text-muted">
              Pick one of the examples above, or write your own. You will get the rows, the SQL that produced
              them, and the tables it read.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

/** Tables a generated query actually reads — pulled from its own FROM / JOIN clauses. */
function tablesIn(sql?: string): string[] {
  if (!sql) return [];
  const refs = [
    ...String(sql).matchAll(/\bfrom\s+([a-z0-9_]+)/gi),
    ...String(sql).matchAll(/\bjoin\s+([a-z0-9_]+)/gi),
  ].map((m) => m[1].toLowerCase());
  return [...new Set(refs)];
}
